import React, { useState } from 'react';
import { ExternalLink, BookOpen, Pencil } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function formatOdds(odds) {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

const SPORTSBOOK_URLS = {
  draftkings: 'https://sportsbook.draftkings.com',
  fanduel: 'https://sportsbook.fanduel.com',
  betmgm: 'https://sports.betmgm.com',
  williamhill_us: 'https://www.caesars.com/sportsbook-and-casino',
  fanatics: 'https://sportsbook.fanatics.com',
  espnbet: 'https://sportsbook.thescore.bet',
  ballybet: 'https://www.ballybet.com',
  betrivers: 'https://www.betrivers.com',
  kalshi: 'https://kalshi.com',
  polymarket: 'https://polymarket.com',
};

// Sport icons based on sport_key prefix
function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

function formatPeriod(period, sportKey) {
  if (!period) return '';
  if (sportKey?.includes('basketball')) return `Q${period}`;
  if (sportKey?.includes('hockey')) return `P${period}`;
  if (sportKey?.includes('baseball')) {
    const sfx = ['st','nd','rd'][period-1] || 'th';
    return `${period}${sfx} Inn`;
  }
  if (sportKey?.includes('football')) return `Q${period}`;
  if (sportKey?.includes('tennis')) return `Set ${period}`;
  return `P${period}`;
}

// Get market display name
function getMarketName(market) {
  if (!market || market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Total';
  if (market === 'player_props') return 'Prop';
  return market;
}

// Get bet description based on market type
function getBetDescription(opp) {
  const market = opp.market || 'h2h';

  if (market === 'player_props') {
    // Player prop format: "Player Name Over/Under Line Stat"
    const propDisplay = opp.market_display || opp.prop_display || opp.prop_type || '';
    return `${opp.player} ${opp.side} ${opp.line} ${propDisplay}`;
  } else if (market === 'h2h') {
    return `${opp.team} ML`;
  } else if (market === 'spreads') {
    const point = opp.point != null ? (opp.point > 0 ? `+${opp.point}` : opp.point) : '';
    return `${opp.team} ${point}`;
  } else if (market === 'totals') {
    const point = opp.point || '';
    return `${opp.team} ${point}`;
  }
  return opp.team;
}

function probToAmerican(probPct) {
  const p = probPct / 100;
  if (p <= 0 || p >= 1) return null;
  return p >= 0.5
    ? Math.round(-(p / (1 - p)) * 100)
    : Math.round(((1 - p) / p) * 100);
}

function EVCardMini({ opportunity, bankroll = 1000, kellyFraction = 0.25, onViewGame, session }) {
  const opp = opportunity;
  const [tracked, setTracked] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [amount, setAmount] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedOdds, setEditedOdds] = useState('');

  async function handleTrack(e) {
    e.stopPropagation();
    if (!session) return;
    try {
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'ev',
          sportsbook: opp.sportsbook_key,
          eventName: opp.event,
          sportKey: opp.sport_key,
          market: opp.market,
          side: opp.side || opp.team || '',
          odds: activeOdds,
          amount: parseFloat(amount) || 0,
          evOpportunityId: opp.id ?? null,
          notes: JSON.stringify({
            event_id: opp.event_id,
            sport_key: opp.sport_key,
            home_team: opp.home_team,
            away_team: opp.away_team,
            side: opp.side || opp.team || '',
            point: opp.point ?? opp.line ?? null,
            ...(opp.market === 'player_props' ? {
              player_name: opp.player,
              prop_type: opp.prop_type,
              commence_time: opp.commence_time,
            } : {}),
          }),
        }),
      });
      setTrackingMode(false);
      setTracked(true);
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  const evLevel = opp.ev_percentage >= 10 ? 'high' :
                  opp.ev_percentage >= 5 ? 'medium' : 'low';

  const sportIcon = getSportIcon(opp.sport_key);
  const marketName = getMarketName(opp.market);
  const betDescription = getBetDescription(opp);

  // Calculate suggested bet: backend full Kelly × user's fractional preference × bankroll
  const suggestedBet = Math.round((opp.kelly_fraction || 0) * kellyFraction * bankroll);

  const trueOdds = opp.consensus_prob != null ? probToAmerican(opp.consensus_prob) : null;

  const parsedEditOdds = parseInt(editedOdds);
  const activeOdds = editMode && !isNaN(parsedEditOdds) && parsedEditOdds !== 0 ? parsedEditOdds : opp.odds;
  const activeDecimal = activeOdds > 0 ? activeOdds / 100 + 1 : 100 / Math.abs(activeOdds) + 1;

  let editedEV = null;
  let editedSuggestedBet = null;
  if (editMode && opp.consensus_prob != null) {
    const p = opp.consensus_prob / 100;
    editedEV = (p * activeDecimal - 1) * 100;
    if (editedEV > 0) {
      const kellyFull = (p * activeDecimal - 1) / (activeDecimal - 1);
      editedSuggestedBet = Math.round(kellyFull * kellyFraction * bankroll);
    }
  }

  const displaySuggestedBet = editMode && editedSuggestedBet !== null ? editedSuggestedBet : suggestedBet;

  const bookCount = opp.book_count || 0;
  const isThinMarket = bookCount > 0 && bookCount < 3;

  return (
    <div className={`ev-card-mini ev-${evLevel}`} onClick={() => onViewGame && onViewGame(opp.event_id, opp.sport_key)}>
      {/* Header: Sport + Market + Favorite Badge */}
      <div className="ev-mini-header">
        <span className="ev-mini-sport">
          <span className="sport-emoji">{sportIcon}</span>
          {opp.sport_title || 'Sports'}
        </span>
        <div className="ev-mini-badges">
          <span className="ev-mini-market-badge">{marketName}</span>
        </div>
      </div>

      {/* EV percentage - hero metric */}
      <div className="ev-mini-value">
        <span className="ev-percentage">
          {editMode && editedEV !== null
            ? `${editedEV >= 0 ? '+' : ''}${editedEV.toFixed(1)}%`
            : `+${opp.ev_percentage}%`}
        </span>
        <span className="ev-label">EV</span>
      </div>

      {/* Game name */}
      <div className="ev-mini-game">
        <span className="mini-game-team"><TeamLogo teamName={opp.home_team} sportKey={opp.sport_key} size={13} />{opp.home_team}</span>
        <span className="mini-game-sep">vs</span>
        <span className="mini-game-team"><TeamLogo teamName={opp.away_team} sportKey={opp.sport_key} size={13} />{opp.away_team}</span>
      </div>
      {!opp.is_live && opp.commence_time && (
        <div className="pc-game-time">
          {new Date(opp.commence_time).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}

      {/* Live score */}
      {opp.is_live && opp.home_score != null && (
        <div className="mini-card-scoreboard">
          <div className="mini-score-teams">
            <span>{(opp.home_team || '').split(' ').pop()} <strong>{opp.home_score}</strong></span>
            <span className="score-sep">·</span>
            <span>{(opp.away_team || '').split(' ').pop()} <strong>{opp.away_score ?? '-'}</strong></span>
          </div>
          {opp.period != null && (
            <span className="mini-score-period">
              {formatPeriod(opp.period, opp.sport_key)}{opp.clock ? ` ${opp.clock}` : ''}
            </span>
          )}
        </div>
      )}

      {/* Bet description */}
      <div className="ev-mini-bet-desc">
        <span className="bet-label">Bet:</span>
        <span className="bet-value">{betDescription}</span>
      </div>

      {/* Player info for props */}
      {opp.market === 'player_props' && opp.player_team && (
        <div className="ev-mini-player-info">
          {opp.player_jersey && <span className="ev-mini-player-jersey">#{opp.player_jersey}</span>}
          <TeamLogo teamName={opp.player_team} sportKey={opp.sport_key} size={13} />
          <span className="ev-mini-player-team">{opp.player_team}</span>
        </div>
      )}

      {/* Odds */}
      <div className="ev-mini-odds">
        <span className="odds-label">Odds:</span>
        {editMode ? (
          <input
            className="odds-edit-input"
            value={editedOdds}
            onChange={e => setEditedOdds(e.target.value)}
            placeholder={String(opp.odds)}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className="odds-value">{formatOdds(opp.odds)}</span>
        )}
      </div>

      {/* True odds */}
      {trueOdds != null && (
        <div className="ev-mini-odds ev-mini-true-odds">
          <span className="odds-label">True odds:</span>
          <span className="odds-value">{formatOdds(trueOdds)}</span>
        </div>
      )}

      {/* Book coverage indicator */}
      {bookCount > 0 && (
        <div className={`ev-mini-coverage ${isThinMarket ? 'thin-market' : ''}`}>
          <BookOpen size={12} />
          <span className="coverage-value">{bookCount} {bookCount === 1 ? 'book' : 'books'}</span>
        </div>
      )}

      {/* Suggested bet amount */}
      {displaySuggestedBet > 0 && (
        <div className="ev-mini-suggested">
          <span className="suggested-label">Suggested:</span>
          <span className="suggested-value">${displaySuggestedBet}</span>
        </div>
      )}

      {/* Sportsbook with logo */}
      <div className="ev-mini-book">
        <SportsbookLogo bookKey={opp.sportsbook_key} size={20} />
        <span className="book-name">@ {SPORTSBOOK_NAMES[opp.sportsbook_key] || opp.sportsbook}</span>
        {SPORTSBOOK_URLS[opp.sportsbook_key] && (
          <a
            href={SPORTSBOOK_URLS[opp.sportsbook_key]}
            target="_blank"
            rel="noopener noreferrer"
            className="book-link"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>

      <div className="edit-odds-row">
        {!editMode && <span className="edit-odds-hint">Seeing different odds?</span>}
        <button
          className="edit-odds-btn"
          onClick={e => {
            e.stopPropagation();
            if (editMode) setEditedOdds('');
            setEditMode(v => !v);
          }}
        >
          <Pencil size={11} />
          {editMode ? 'Reset odds' : 'Edit here'}
        </button>
      </div>
      {session && !tracked && !trackingMode && (
        <button
          className="ev-track-btn"
          onClick={(e) => { e.stopPropagation(); setAmount(displaySuggestedBet ? String(displaySuggestedBet) : ''); setTrackingMode(true); }}
        >
          Track Bet
        </button>
      )}
      {session && trackingMode && (
        <div className="track-amount-form" onClick={e => e.stopPropagation()}>
          <input
            type="number"
            className="track-amount-input"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Stake ($)"
            min="0"
            step="1"
            autoFocus
          />
          <button className="track-amount-save" onClick={handleTrack}>Save</button>
          <button className="track-amount-cancel" onClick={(e) => { e.stopPropagation(); setTrackingMode(false); }}>✕</button>
          {parseFloat(amount) > 0 && (() => {
            const profit = parseFloat(amount) * (activeOdds > 0 ? activeOdds / 100 : 100 / Math.abs(activeOdds));
            return <span className="ctf-to-win">To win: +${profit.toFixed(2)}</span>;
          })()}
        </div>
      )}
      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" onClick={e => e.stopPropagation()} disabled>Tracked ✓</button>
      )}

    </div>
  );
}

export default EVCardMini;
